import {useContext, useRef, useState} from 'react';
import axios, {HttpStatusCode} from 'axios';
import CarecoApi, {SecureEmailRequestError} from '@/api/careco-api';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {CareJourneyDetailsResponse} from '@/api/dto/care-journey';
import {RosterFormCodeBinding} from '@/api/dto/forms';
import {Roster} from '@/api/dto/dto';
import Helpers from '@/helpers/helpers';
import {
  createUlid,
  patientEmailFromFormData,
  secureEmailVariables,
} from '@/helpers/secure_email_helpers';

function errorMessage(error: unknown): string {
  if (error instanceof SecureEmailRequestError) {
    if (error.status === HttpStatusCode.Unauthorized) {
      return 'Your session has expired. Please sign in again before scheduling via email.';
    }
    if (error.status === HttpStatusCode.Forbidden) {
      return 'You do not have access to the Secure Email inbox for this roster.';
    }
    if (error.status >= HttpStatusCode.InternalServerError) {
      return 'Secure Email is temporarily unavailable. Please try again later.';
    }
  }
  return 'Schedule via Email failed.';
}

export function useScheduleViaEmail() {
  const {carecoApi} = useContext(CarecoApiContext);
  const [isPending, setIsPending] = useState(false);
  const inFlight = useRef(false);
  // Reused for retries on the same journey so secure-mail returns the existing draft.
  const idempotency = useRef<{journeyId: string; key: string} | undefined>(undefined);

  const createDraft = async (
    api: CarecoApi,
    careJourney: CareJourneyDetailsResponse,
    roster: Roster,
    intakeData: Record<string, unknown> | undefined,
    newTab: Window,
  ) => {
    inFlight.current = true;
    setIsPending(true);
    try {
      const [inbox] = await api.getSecureEmailInboxes(roster.id);
      if (!inbox) throw new Error('No Secure Email inbox is configured for this roster.');

      let patientEmail = '';
      try {
        const form = await api.getFormByRosterCodeBindingAndPatientId(
          RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS,
          careJourney.clientProfile.patient.patientId,
          roster.id,
        );
        patientEmail = patientEmailFromFormData(await api.getSurveyFormData(form.id, roster.id));
      } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== HttpStatusCode.NotFound) throw error;
      }

      if (idempotency.current?.journeyId !== careJourney.careJourneyInstanceId) {
        idempotency.current = {journeyId: careJourney.careJourneyInstanceId, key: createUlid()};
      }
      const draft = await api.createSecureEmailDraft({
        inbox_id: inbox.inboxId,
        template_id: 'scheduling',
        variables: secureEmailVariables(careJourney, patientEmail, intakeData),
        source: 'bih',
        source_context: {
          care_journey_id: careJourney.careJourneyInstanceId,
          patient_id: careJourney.clientProfile.patient.patientId,
          roster_id: String(roster.id),
        },
      }, idempotency.current.key);

      newTab.opener = null;
      newTab.location.href = new URL(draft.compose_url, process.env.REACT_APP_SECURE_EMAIL_PORTAL_URL).toString();
    } catch (error) {
      newTab.close();
      Helpers.alertOnError(errorMessage(error), error instanceof Error ? error : String(error));
    } finally {
      inFlight.current = false;
      setIsPending(false);
    }
  };

  // intakeData is passed in because BH_SCHEDULE_APPOINTMENT journeys keep intake on the parent journey.
  const scheduleViaEmail = (
    careJourney: CareJourneyDetailsResponse,
    roster: Roster,
    intakeData?: Record<string, unknown>,
  ) => {
    if (!carecoApi || inFlight.current) return;

    // Open the tab synchronously in the click handler so popup blockers allow it.
    const newTab = window.open();
    if (!newTab) {
      Helpers.alertOnError('Secure Email could not be opened.', new Error('The browser blocked the new tab.'));
      return;
    }
    newTab.document.write('Loading Secure Email...');
    void createDraft(carecoApi, careJourney, roster, intakeData, newTab);
  };

  return {scheduleViaEmail, isPending};
}
